/**
 * @file Settings service.
 * @description Manages system settings payloads, history retrieval, and settings-related utility actions.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { apiFetch } from '../api/api';
import { API_ENDPOINTS } from '../constants/api';
import type {
  SettingsData,
  SettingsApiResponse,
  SettingsSaveResponse,
  HistoryClearResponse,
  TimeZoneApiResponse,
  TimeZoneOption,
  TimeZoneSettingsResult,
} from '../types/api/settings';

export type {
  SettingsData,
  SettingsApiResponse,
  SettingsSaveResponse,
  HistoryClearResponse,
  TimeZoneOption,
  TimeZoneSettingsResult,
} from '../types/api/settings';
export type { HistoryItem } from '../types/api/settings';

const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

const toTimeZoneOption = (value: string, label?: string): TimeZoneOption => ({
  value,
  label: label ?? value,
});

const parseTimeZoneList = (items: unknown[]): TimeZoneOption[] => {
  const seen = new Set<string>();
  const options: TimeZoneOption[] = [];

  for (const item of items) {
    let option: TimeZoneOption | null = null;

    if (typeof item === 'string' && item.trim()) {
      option = toTimeZoneOption(item.trim());
    } else if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const value =
        obj['value'] ?? obj['id'] ?? obj['time_zone'] ?? obj['timezone'] ?? obj['name'];
      if (typeof value === 'string' && value.trim()) {
        const label = obj['label'] ?? obj['name'] ?? obj['display'] ?? value;
        option = toTimeZoneOption(
          value.trim(),
          typeof label === 'string' ? label : String(label),
        );
      }
    }

    if (option && !seen.has(option.value)) {
      seen.add(option.value);
      options.push(option);
    }
  }

  return options;
};

export class SettingsService {
  static parseTimeZoneResponse(response: TimeZoneApiResponse): TimeZoneSettingsResult {
    const data = response.data;
    let selected = DEFAULT_TIME_ZONE;
    let options: TimeZoneOption[] = [];

    if (Array.isArray(data)) {
      options = parseTimeZoneList(data);
    } else if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const listKeys = [
        'time_zones',
        'timezones',
        'zones',
        'list',
        'available_time_zones',
        'available',
      ];

      for (const key of listKeys) {
        if (Array.isArray(obj[key])) {
          options = parseTimeZoneList(obj[key] as unknown[]);
          break;
        }
      }

      const selectedKeys = ['time_zone', 'timezone', 'selected', 'current', 'value'];
      for (const key of selectedKeys) {
        if (typeof obj[key] === 'string' && obj[key]) {
          selected = obj[key] as string;
          break;
        }
      }
    } else if (typeof data === 'string' && data) {
      selected = data;
    }

    if (options.length === 0) {
      options = [toTimeZoneOption(selected)];
    }

    if (!options.some((option) => option.value === selected)) {
      options = [toTimeZoneOption(selected), ...options];
    }

    return { selected, options };
  }

  static ensureTimeZoneInOptions(
    options: TimeZoneOption[],
    current: string,
  ): TimeZoneOption[] {
    if (options.some((option) => option.value === current)) {
      return options;
    }
    return [toTimeZoneOption(current), ...options];
  }

  /**
   * Fetch time zone list and current selection from API
   */
  static async getTimeZoneSettings(): Promise<TimeZoneSettingsResult> {
    const response = await apiFetch<TimeZoneApiResponse>(
      API_ENDPOINTS.SETTINGS_TIME_ZONE,
      'GET',
    );
    return SettingsService.parseTimeZoneResponse(response);
  }
  /**
   * Fetch settings and history data
   */
  static async getSettings(): Promise<SettingsApiResponse> {
    try {
      const response = await apiFetch<SettingsApiResponse>(API_ENDPOINTS.SETTINGS, 'GET');
      
      // Handle error response
      if (response.status === 'error') {
        // Return default data structure for error cases
        return {
          status: 'error',
          message: response.message,
          data: {
            settings: {
              export_log_days: '7',
              duplicate_check: '30',
              max_file_size: '10',
              time_zone: 'Asia/Kolkata',
              auto_validation: '1',
              email_notification: '0',
            },
            recentUploads: {},
            recentExports: {},
          },
        };
      }
      
      // Ensure data exists and has required structure
      if (!response.data) {
        return {
          status: 'success',
          message: 'Using default settings',
          data: {
            settings: {
              export_log_days: '7',
              duplicate_check: '30',
              max_file_size: '10',
              time_zone: 'Asia/Kolkata',
              auto_validation: '1',
              email_notification: '0',
            },
            recentUploads: {},
            recentExports: {},
          },
        };
      }
      
      return response;
    } catch {
      // Return default data structure for network errors
      return {
        status: 'error',
        message: 'Failed to fetch settings data',
        data: {
          settings: {
            export_log_days: '7',
            duplicate_check: '30',
            max_file_size: '10',
            time_zone: 'Asia/Kolkata',
            auto_validation: '1',
            email_notification: '0',
          },
          recentUploads: {},
          recentExports: {},
        },
      };
    }
  }

  /**
   * Save settings
   */
  static async saveSettings(settings: SettingsData): Promise<SettingsSaveResponse> {
    try {
      const response = await apiFetch<SettingsSaveResponse>(
        API_ENDPOINTS.SETTINGS,
        'POST',
        settings as unknown as Record<string, unknown>,
      );
      return response;
    } catch {
      throw new Error('Failed to save settings');
    }
  }

  /**
   * Clear history (upload or export)
   */
  static async clearHistory(type: 'upload' | 'export'): Promise<HistoryClearResponse> {
    try {
      const response = await apiFetch<HistoryClearResponse>(API_ENDPOINTS.SETTINGS, 'DELETE', { type });
      return response;
    } catch {
      throw new Error(`Failed to clear ${type} history`);
    }
  }

  /**
   * Re-export a file
   */
  static async reExportFile(campaignId: string, filename: string): Promise<Blob> {
    try {
      const response = await fetch(`${API_ENDPOINTS.LEADS_EXPORT}?action=export&campaign=${campaignId}&filename=${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jiggle_token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Re-export failed');
      }

      return await response.blob();
    } catch {
      throw new Error('Failed to re-export file');
    }
  }

  /**
   * Download file from blob
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Validate settings data
   */
  static validateSettings(settings: Partial<SettingsData>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const exportLogDays = settings.export_log_days ? Number.parseInt(settings.export_log_days, 10) : null;
    const duplicateCheck = settings.duplicate_check ? Number.parseInt(settings.duplicate_check, 10) : null;
    const maxFileSize = settings.max_file_size ? Number.parseInt(settings.max_file_size, 10) : null;

    if (exportLogDays !== null && (Number.isNaN(exportLogDays) || exportLogDays < 1 || exportLogDays > 365)) {
      errors.push('Export lock days must be between 1 and 365');
    }

    if (duplicateCheck !== null && (Number.isNaN(duplicateCheck) || duplicateCheck < 1 || duplicateCheck > 365)) {
      errors.push('Duplicate check days must be between 1 and 365');
    }

    if (maxFileSize !== null && (Number.isNaN(maxFileSize) || maxFileSize < 1 || maxFileSize > 1000)) {
      errors.push('Maximum file size must be between 1 and 1000 MB');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
